<template>
  <!-- Admin Utilities — no Figma frame; built in the Service & Repair card style.
       Items and flows follow the Android technician app (AdminUtilsFragment): Update Order (price / summary /
       installation date / address), Get Customer Phone Verification Code, Heat Load Calculator. APIs: api/v3/pro/utilities/*. -->
  <div class="w-full font-roboto">
    <div v-if="!isAllowed" class="auCard text-center py-[40px]">
      <p class="m-0 text-[16px] text-[#333333]">Admin Utilities is not available for your account.</p>
    </div>

    <div v-else class="grid grid-cols-1 laptop:grid-cols-2 gap-[20px] items-start">
      <!-- Update Order (Android UpdateOrderFragment) -->
      <section class="auCard laptop:row-span-2">
        <div class="auTitle">
          <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <h2>Update Order</h2>
        </div>
        <p class="auHint">To update an order, please enter the Order ID below</p>
        <label class="auLabel" for="auOrderId">Order ID</label>
        <input id="auOrderId" v-model.trim="orderId" type="text" inputmode="numeric" maxlength="12"
          placeholder="Enter ID" class="auInput" @input="orderId = orderId.replace(/\D/g, '')">

        <div class="grid grid-cols-1 tablet:grid-cols-2 gap-[12px] mt-[20px]">
          <button v-for="action in orderActions" :key="action.key" type="button" class="auTile" @click="runOrderAction(action.key)">
            <span class="auTileIcon"><img :src="action.icon" alt=""></span>
            <span class="text-left">
              <span class="block text-[15px] font-medium text-[#333333]">{{ action.label }}</span>
              <span class="block text-[12px] text-[#666666] mt-[2px]">{{ action.hint }}</span>
            </span>
          </button>
        </div>
      </section>

      <!-- Get Customer Phone Verification Code (Android GetCustomerPhoneVerificationCodeFragment) -->
      <section class="auCard">
        <div class="auTitle">
          <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <h2>Customer Phone Verification Code</h2>
        </div>
        <p class="auHint">Please enter consumer's phone number to obtain the verification code</p>
        <label class="auLabel" for="auPhone">Phone Number</label>
        <div class="flex flex-col tablet:flex-row gap-[10px]">
          <input id="auPhone" v-model="phone" v-maska="'(###) ###-####'" type="tel" placeholder="Enter Number"
            class="auInput tablet:flex-1" @keyup.enter="getVerificationCode">
          <button type="button" class="fxBtnPrimary" :disabled="busy" @click="getVerificationCode">Get Code</button>
        </div>
        <div v-if="verificationCode" class="mt-[15px] rounded-[10px] border-1 border-[#C3DAFB] bg-[#EAF2FE] px-[16px] py-[12px] flex items-center justify-between gap-[10px]">
          <div>
            <span class="block text-[13px] text-[#2A5BA8]">Verification Code</span>
            <span class="block text-[24px] font-semibold tracking-[4px] text-[#333333]">{{ verificationCode }}</span>
          </div>
          <button type="button" class="fxBtnSecondary !px-[15px]" @click="copyCode">Copy</button>
        </div>
      </section>

      <!-- Heat Load Calculator (Android opens the page in the browser) -->
      <section class="auCard">
        <div class="auTitle">
          <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <h2>Heat Load Calculator</h2>
        </div>
        <div class="flex flex-col tablet:flex-row tablet:items-center gap-[15px]">
          <span class="auTileIcon"><img :src="icons.heatLoad" alt=""></span>
          <p class="m-0 flex-1 text-[14px] text-[#666666]">Estimate the heating and cooling load of a home before designing a system.</p>
          <a :href="heatLoadUrl" target="_blank" rel="noopener noreferrer" class="fxBtnPrimary no-underline">Open Calculator</a>
        </div>
      </section>
    </div>

    <!-- one right-pinned modal, content by type (house style: FxModalHeader, one column, footer pinned) -->
    <div ref="auModal" class="modal fade" tabindex="-1" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <div class="modal-content">
          <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
            <FxModalHeader :title="modal.title" :subtitle="modal.orderId ? `Order ID: ${modal.orderId}` : ''" :dismiss="false" @close="closeModal" />

            <div class="flex-1 flex flex-col gap-[12px] px-[6px]">
              <!-- Update Order Price (Android UpdateOrderPriceFragment) -->
              <template v-if="modal.type === 'price'">
                <div>
                  <span class="uqcLabel">Order Price</span>
                  <p class="auValue">{{ money(price.total) }}</p>
                </div>
                <div>
                  <span class="uqcLabel">Payment Type</span>
                  <p class="auValue">{{ currentPaymentTypeText }}</p>
                </div>
                <div>
                  <label class="uqcLabel" for="auNewPrice">New Price ($) <span class="text-[#EE5851]">*</span></label>
                  <input id="auNewPrice" v-model="form.newPrice" type="text" inputmode="decimal" placeholder="Price" class="auInput"
                    @input="form.newPrice = form.newPrice.replace(/[^\d.]/g, '')">
                  <p class="m-0 mt-[4px] text-[12px] text-[#666666]">(This price is exclusive of taxes)</p>
                </div>
                <div>
                  <span class="uqcLabel">New Payment Type <span class="text-[#EE5851]">*</span></span>
                  <FxSelect v-model="form.paymentType" :options="price.payment_types || []" label-key="value" value-key="key"
                    placeholder="Select payment type" inline />
                </div>
              </template>

              <!-- Change Order Installation Date (Android ChangeOrderInstallationDateFragment: date, then time 7 AM–9 PM, 15 min) -->
              <template v-else-if="modal.type === 'dates'">
                <div v-for="field in dateFields" :key="field.key">
                  <span class="uqcLabel">{{ field.label }}</span>
                  <date-picker v-model:value="form[field.key]" class="auDate" :type="field.withTime ? 'datetime' : 'date'"
                    :format="field.withTime ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY'" :placeholder="'Select ' + field.label"
                    :editable="false" :clearable="false" :confirm="field.withTime" :show-second="false" :minute-step="15"
                    :disabled-date="beforeToday" :disabled-time="field.withTime ? outsideWorkHours : null" :append-to-body="false" />
                </div>
                <p class="m-0 text-[12px] text-[#666666]">Inspection and installation times are between 7:00 AM and 9:00 PM.</p>
              </template>

              <!-- Update Order Address (Android UpdateOrderAddressFragment): City / State / Zip come from the picked address -->
              <template v-else-if="modal.type === 'address'">
                <!-- the order's address now, on its own; the fields below are the new one -->
                <div class="rounded-[10px] border-1 border-[#E1E5EF] bg-[#F7F8FB] px-[12px] py-[10px]">
                  <span class="uqcLabel">Current Address</span>
                  <p class="auValue !text-[14px]">{{ currentAddressText || '-' }}</p>
                </div>
                <div>
                  <span class="uqcLabel">New Street Address <span class="text-[#EE5851]">*</span></span>
                  <GoogleAutoComplete v-model="form.address" placeholder="Enter Address"
                    :input-class="'w-full h-[38px] px-[12px] rounded-[8px] text-[14px] text-[#333333] outline-none'"
                    @place-selected="handlePlace" />
                </div>
                <div>
                  <label class="uqcLabel" for="auCity">City</label>
                  <input id="auCity" :value="form.city" type="text" placeholder="City" class="auInput" readonly>
                </div>
                <div>
                  <label class="uqcLabel" for="auState">State</label>
                  <input id="auState" :value="form.state" type="text" placeholder="State" class="auInput" readonly>
                </div>
                <div>
                  <label class="uqcLabel" for="auZip">Zip Code</label>
                  <input id="auZip" :value="form.postal_code" type="text" placeholder="Zip Code" class="auInput" readonly>
                </div>
              </template>
            </div>

            <div class="uqcFooter flex items-center justify-end gap-[10px]">
              <button type="button" class="uqcBtnCancel" @click="closeModal">Cancel</button>
              <button type="button" class="uqcBtnNext" :disabled="busy" @click="submitModal">
                {{ modal.type === 'price' ? 'Submit' : 'Update' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import { mapGetters } from "vuex";
import { useToast } from "vue-toastification";
import { vMaska } from "maska/vue";
import FxModalHeader from "../../components/nest_components/FxModalHeader.vue";
import GoogleAutoComplete from "../../components/partialComponent/GoogleAutoComplete.vue";
import FxSelect from "../../components/partialComponent/FxSelect.vue";
import { adminUtilitiesApi, HEAT_LOAD_CALCULATOR_URL } from "../../services/adminUtilitiesApi";
import { menuHasRoute } from "../../helpers/techMenu";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";
import priceIcon from "../../assets/images/admin-utilities/price.png";
import summaryIcon from "../../assets/images/admin-utilities/edit-summary.png";
import calendarIcon from "../../assets/images/admin-utilities/calendar-black.png";
import mapIcon from "../../assets/images/admin-utilities/map.png";
import heatLoadIcon from "../../assets/images/admin-utilities/heat-load.png";

const toast = useToast();

// unix seconds ↔ Date for the pickers
const toDate = (seconds) => (Number(seconds) ? new Date(Number(seconds) * 1000) : null);
const toUnix = (date) => Math.floor(date.getTime() / 1000);

export default {
  name: "AdminUtilities",
  components: { FxModalHeader, GoogleAutoComplete, FxSelect, DatePicker },
  directives: { maska: vMaska },
  data() {
    return {
      orderId: "",
      phone: "",
      verificationCode: "",
      busy: false,
      heatLoadUrl: HEAT_LOAD_CALCULATOR_URL,
      icons: { heatLoad: heatLoadIcon },
      orderActions: [
        { key: "price", label: "Update Price", hint: "New price and payment type", icon: priceIcon },
        { key: "summary", label: "Edit Summary", hint: "System summary of the order, with Edit Summary", icon: summaryIcon },
        { key: "dates", label: "Change Installation Date", hint: "Inspection, installation and invoice dates", icon: calendarIcon },
        { key: "address", label: "Update Address", hint: "Service address of the order", icon: mapIcon },
      ],
      dateFields: [
        { key: "inspection_datetime", label: "Inspection Date", withTime: true },
        { key: "installation_datetime", label: "Installation Date", withTime: true },
        { key: "invoice_datetime", label: "Invoice Date", withTime: false },
        { key: "invoice_paid_datetime", label: "Invoice Paid Date", withTime: false },
      ],
      modal: { type: "", title: "", orderId: "" },
      form: {},
      price: {},
      dates: {},
      address: {},
    };
  },
  computed: {
    ...mapGetters(["sideMenuData"]),
    // the side menu is role-filtered on the server (G-25); a direct URL follows it once the menu has loaded
    isAllowed() {
      return !Array.isArray(this.sideMenuData) || menuHasRoute(this.sideMenuData, "/admin-utilities");
    },
    currentAddressText() {
      const a = this.address || {};
      const tail = [a.city, [a.state, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      // the stored address is often already the full line; add city / state / zip only when they aren't in it
      return a.address && tail && !String(a.address).includes(String(a.city || "")) ? `${a.address}, ${tail}` : (a.address || tail);
    },
    currentPaymentTypeText() {
      const p = this.price;
      const match = (p.payment_types || []).find(t => (t.key || "").toLowerCase() === (p.preferred_payment_type || "").toLowerCase());
      return match?.value || p.preferred_payment_type_text || (p.preferred_payment_type || "-").toUpperCase();
    },
  },
  beforeUnmount() {
    this.bsModal?.dispose();
  },
  methods: {
    money(value) {
      const n = Number(value) || 0;
      return `$ ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    errorOf(res, fallback) {
      return (typeof res.body === "string" && res.body) || res.message || fallback;
    },
    showModal() {
      this.$nextTick(() => {
        if (!this.bsModal) {
          this.bsModal = new Modal(this.$refs.auModal, { backdrop: "static", keyboard: false });
          this.$refs.auModal.addEventListener("hidden.bs.modal", () => {
            enableScroll();
            this.modal = { type: "", title: "", orderId: "" };
          });
        }
        disableScroll();
        this.bsModal.show();
      });
    },
    closeModal() {
      this.bsModal?.hide();
    },
    openModal(type, title, orderId) {
      this.modal = { type, title, orderId: String(orderId || "") };
      this.showModal();
    },
    submitModal() {
      if (this.modal.type === "price") return this.confirmPrice();
      if (this.modal.type === "dates") return this.submitDates();
      if (this.modal.type === "address") return this.submitAddress();
    },
    beforeToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },
    // Android time picker: 7:00 AM – 9:00 PM
    outsideWorkHours(date) {
      const h = date.getHours();
      return h < 7 || h > 21 || (h === 21 && date.getMinutes() > 0);
    },

    // Android UpdateOrderFragment: every action needs the Order ID first
    runOrderAction(key) {
      if (!this.orderId) {
        toast.error("Order ID must be entered to perform this operation");
        return;
      }
      if (key === "price") return this.openPrice();
      if (key === "summary") return this.openSummary();
      if (key === "dates") return this.openDates();
      if (key === "address") return this.openAddress();
    },

    async openPrice() {
      const res = await adminUtilitiesApi.orderPrice(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Unable to get the order price."));
      this.setPrice(res.body);
      this.openModal("price", "Update Order Price", res.body.id);
    },
    setPrice(body) {
      this.price = body || {};
      const preferred = (this.price.preferred_payment_type || "").toLowerCase();
      const match = (this.price.payment_types || []).find(t => (t.key || "").toLowerCase() === preferred);
      this.form = {
        newPrice: (Number(this.price.total) || 0).toFixed(2),
        paymentType: match ? match.key : "",
      };
    },
    // validate, then "Are you sure" before the price changes
    confirmPrice() {
      if (!this.form.newPrice) return toast.error("Enter New Price");
      if (!this.form.paymentType) return toast.error("Enter Preferred Payment Type");
      const type = (this.price.payment_types || []).find(t => String(t.key) === String(this.form.paymentType));
      this.$dialog.show({
        title: "Update Price",
        text: `Are you sure you want to update the price of order ${this.price.id} to ${this.money(this.form.newPrice)}${type ? ` (${type.value})` : ""}?`,
        buttonText: "Confirm",
        onConfirm: () => this.submitPrice(),
      });
    },
    async submitPrice() {
      this.busy = true;
      const res = await adminUtilitiesApi.updateOrderPrice(this.price.id, this.form.newPrice.replace(/[$,]/g, ""), this.form.paymentType);
      this.busy = false;
      if (!res.ok) return toast.error(this.errorOf(res, "Unable to update the price."));
      this.setPrice(res.body);
      toast.success(res.message || "Price has been updated successfully.");
    },

    // our System Summary page (Edit Summary on its band); the utilities call checks the order exists first
    async openSummary() {
      const res = await adminUtilitiesApi.orderSummaryEdit(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Oops! Something Went Wrong"));
      this.$router.push({ path: `/replacement-quote/summary/${res.body.id || this.orderId}`, query: { from: "admin" } });
    },

    async openDates() {
      const res = await adminUtilitiesApi.orderDates(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Unable to get the order dates."));
      this.dates = res.body;
      this.form = {};
      this.dateFields.forEach(f => { this.form[f.key] = toDate(res.body[f.key]); });
      this.openModal("dates", "Change Order Installation Date", res.body.id);
    },
    async submitDates() {
      const params = {};
      for (const f of this.dateFields) {
        const date = this.form[f.key];
        const original = this.dates[f.key];
        if (!date) continue;
        // unchanged → send what the server gave (Android sends the stored value back)
        if (Number(original) && toUnix(date) === Number(original)) {
          params[f.key] = String(original);
          continue;
        }
        if (this.beforeToday(date)) return toast.error(`${f.label} cannot be in the past.`);
        if (f.withTime && this.outsideWorkHours(date)) return toast.error(`${f.label} time must be between 7:00 AM and 9:00 PM.`);
        // a date-only pick keeps the current time of day, like the Android date picker
        const value = new Date(date);
        if (!f.withTime) {
          const now = new Date();
          value.setHours(now.getHours(), now.getMinutes(), 0, 0);
        }
        params[f.key] = String(toUnix(value));
      }
      this.busy = true;
      const res = await adminUtilitiesApi.updateOrderDates(this.dates.id, params);
      this.busy = false;
      if (!res.ok) return toast.error(this.errorOf(res, "Unable to update the dates."));
      Object.assign(this.dates, params);
      toast.success(res.body?.msg || res.message || "Dates have been updated successfully.");
    },

    async openAddress() {
      const res = await adminUtilitiesApi.orderAddress(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Unable to get the order address."));
      this.setAddress(res.body);
      this.openModal("address", "Update Order Address", res.body.order_id);
    },
    setAddress(body) {
      this.address = body || {};
      // new address starts empty: City / State / Zip fill in from the picked address
      this.form = { address: "", city: "", state: "", postal_code: "", lat: null, long: null };
    },
    // Google place → address parts (Android getAddress → formatted/address/get)
    async handlePlace(place) {
      const lat = place?.geometry?.location?.lat?.();
      const lng = place?.geometry?.location?.lng?.();
      const res = await adminUtilitiesApi.formattedAddress({ address: place?.formatted_address, lat, lng });
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Some thing went wrong"));
      this.form = {
        ...this.form,
        address: res.body.address_1 || place?.formatted_address || this.form.address,
        city: res.body.city_id || "",
        state: res.body.state_id || "",
        postal_code: res.body.postal_code || "",
        lat: lat ?? null,
        long: lng ?? null,
      };
    },
    async submitAddress() {
      const f = this.form;
      if (!f.address?.trim()) return toast.error("Please enter street address to proceed further");
      if (f.lat == null) return toast.error("Please pick the new address from the suggestions");
      if (!f.city?.trim()) return toast.error("Please enter city name to proceed further");
      if (!f.state?.trim()) return toast.error("Please enter the state to proceed further");
      if (!f.postal_code?.trim()) return toast.error("Please enter the zip code to proceed further");
      this.busy = true;
      const res = await adminUtilitiesApi.updateOrderAddress(this.address.order_id, {
        id: this.address.id,
        order_id: this.address.order_id,
        address: f.address.trim(),
        lat: f.lat,
        long: f.long,
        city: f.city.trim(),
        state: f.state.trim(),
        postal_code: f.postal_code.trim(),
      });
      this.busy = false;
      if (!res.ok) return toast.error(this.errorOf(res, "Unable to update the address."));
      this.setAddress(res.body);
      toast.success(res.message || "Address has been updated successfully.");
    },

    async getVerificationCode() {
      this.verificationCode = "";
      if (!this.phone.trim()) return toast.error("Phone Number must be entered to perform this operation");
      this.busy = true;
      const res = await adminUtilitiesApi.verificationCode(this.phone);
      this.busy = false;
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "No verification code found against this number"));
      this.verificationCode = String(res.body.code ?? "");
    },
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.verificationCode);
        toast.success("Verification code copied.");
      } catch (e) {
        toast.error("Unable to copy the code.");
      }
    },
  },
};
</script>

<style scoped>
.auCard {
  box-shadow: 0px 0px 10px 0px #0000001A;
  border-radius: 20px;
  padding: 20px 15px;
  background: #fff;
}

.auTitle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 15px;
}

.auTitle h2 {
  margin: 0;
  padding-left: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.auHint {
  margin: 0 0 15px;
  font-size: 14px;
  color: #666666;
}

.auLabel {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #EA8A8A;
}

.auValue {
  margin: 0;
  font-size: 16px;
  color: #333333;
}

.auInput {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E2E5EC;
  border-radius: 8px;
  font-size: 14px;
  color: #333333;
  outline: none;
  background: #fff;
}

.auInput:focus {
  border-color: #7695FF;
}

/* same 40px field as the inputs; the popup opens under the field and never wider than the screen */
.auDate {
  position: relative;
  width: 100%;
}
.auDate :deep(.mx-input) {
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #E2E5EC;
  font-size: 14px;
  color: #333333;
  box-shadow: none;
}
.auDate :deep(.mx-datepicker-popup) {
  top: 100% !important;
  left: 0 !important;
  bottom: auto !important;
  margin-top: 4px;
  z-index: 20;
  max-width: calc(100vw - 32px);
  overflow-x: auto;
}

.auTile {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid #EFEFEF;
  border-radius: 12px;
  background: #fff;
  transition: border-color .2s, box-shadow .2s;
}

.auTile:hover {
  border-color: #7695FF;
  box-shadow: 0px 0px 10px 0px #0000001A;
}

.auTileIcon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F4F5FB;
}

.auTileIcon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

@media (min-width: 1024px) {
  .auCard {
    padding: 20px;
  }

  .auTitle h2 {
    font-size: 20px;
  }

  .auLabel {
    font-size: 13px;
  }
}

@media (min-width: 1536px) {
  .auLabel {
    font-size: 14px;
  }
}
</style>
